// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** A person whose recipes you keep. */
export const cooks = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededCook = demoData([
  { id: -1, name: 'Cook 1', relationship: 'mum', portraitUrl: 'Sample portraitUrl', kitchen: 'Sample kitchen', addedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, name: 'Cook 2', relationship: 'dad', portraitUrl: 'Sample portraitUrl', kitchen: 'Sample kitchen', addedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, name: 'Cook 3', relationship: 'nonna', portraitUrl: 'Sample portraitUrl', kitchen: 'Sample kitchen', addedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededCook, (rows) => {
  if (rows.length && !cooks.value.length) cooks.value = [...rows];
}, { immediate: true });

export const cooksReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'cook');
  if (mine.length) cooks.value = mine;
  return cooks.value;
});

export function addCook(input = {}) {
  const row = { id: nextId(), entity: 'cook',
    name: input.name ?? '',
    relationship: input.relationship ?? '',
    portraitUrl: input.portraitUrl ?? '',
    kitchen: input.kitchen ?? '',
    addedAt: input.addedAt ?? new Date().toISOString(),
  };
  cooks.value = [row, ...cooks.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveCook(row) {
  cooks.value = cooks.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeCook(id) {
  cooks.value = cooks.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findCook = (id) =>
  cooks.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentCook = computed(() => cooks.value.slice(0, 3));
export const cooksTitle = (row) => row?.name;

/** Recipe */
export const recipes = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededRecipe = demoData([
  { id: -1, title: 'Recipe 1', cookId: -1, occasion: 'Sample occasion', serves: 1, status: 'capturing', capturedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, title: 'Recipe 2', cookId: -2, occasion: 'Sample occasion', serves: 2, status: 'kept', capturedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, title: 'Recipe 3', cookId: -3, occasion: 'Sample occasion', serves: 3, status: 'capturing', capturedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededRecipe, (rows) => {
  if (rows.length && !recipes.value.length) recipes.value = [...rows];
}, { immediate: true });

export const recipesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'recipe');
  if (mine.length) recipes.value = mine;
  return recipes.value;
});

export function addRecipe(input = {}) {
  const row = { id: nextId(), entity: 'recipe',
    title: input.title ?? '',
    cookId: input.cookId ?? '',
    occasion: input.occasion ?? '',
    serves: input.serves ?? 0,
    status: input.status ?? '',
    capturedAt: input.capturedAt ?? new Date().toISOString(),
  };
  recipes.value = [row, ...recipes.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveRecipe(row) {
  recipes.value = recipes.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeRecipe(id) {
  recipes.value = recipes.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findRecipe = (id) =>
  recipes.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentRecipe = computed(() => recipes.value.slice(0, 3));
export const recipesTitle = (row) => row?.title;

/** One line of the ingredient list. Carries a real measured amount when there is one, and the cook's own words when there isn't. */
export const ingredients = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededIngredient = demoData([
  { id: -1, recipeId: -1, amount: 1, unit: 'Sample unit', quantity: 'Sample quantity', item: 'Sample item', position: 1 },
  { id: -2, recipeId: -2, amount: 2, unit: 'Sample unit', quantity: 'Sample quantity', item: 'Sample item', position: 2 },
  { id: -3, recipeId: -3, amount: 3, unit: 'Sample unit', quantity: 'Sample quantity', item: 'Sample item', position: 3 },
]);
watch(seededIngredient, (rows) => {
  if (rows.length && !ingredients.value.length) ingredients.value = [...rows];
}, { immediate: true });

export const ingredientsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'ingredient');
  if (mine.length) ingredients.value = mine;
  return ingredients.value;
});

export function addIngredient(input = {}) {
  const row = { id: nextId(), entity: 'ingredient',
    recipeId: input.recipeId ?? '',
    amount: input.amount ?? 0,
    unit: input.unit ?? '',
    quantity: input.quantity ?? '',
    item: input.item ?? '',
    position: input.position ?? 0,
  };
  ingredients.value = [row, ...ingredients.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveIngredient(row) {
  ingredients.value = ingredients.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeIngredient(id) {
  ingredients.value = ingredients.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findIngredient = (id) =>
  ingredients.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentIngredient = computed(() => ingredients.value.slice(0, 3));
export const ingredientsTitle = (row) => row?.unit;

/** Step */
export const steps = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededStep = demoData([
  { id: -1, recipeId: -1, body: 'Sample body', position: 1 },
  { id: -2, recipeId: -2, body: 'Sample body', position: 2 },
  { id: -3, recipeId: -3, body: 'Sample body', position: 3 },
]);
watch(seededStep, (rows) => {
  if (rows.length && !steps.value.length) steps.value = [...rows];
}, { immediate: true });

export const stepsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'step');
  if (mine.length) steps.value = mine;
  return steps.value;
});

export function addStep(input = {}) {
  const row = { id: nextId(), entity: 'step',
    recipeId: input.recipeId ?? '',
    body: input.body ?? '',
    position: input.position ?? 0,
  };
  steps.value = [row, ...steps.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveStep(row) {
  steps.value = steps.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeStep(id) {
  steps.value = steps.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findStep = (id) =>
  steps.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentStep = computed(() => steps.value.slice(0, 3));
export const stepsTitle = (row) => row?.body;

/** An aside the cook made while cooking, pinned to the step it belongs to. stepId is optional: a note recorded before the first step belongs to the recipe. */
export const marginNotes = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededMarginNote = demoData([
  { id: -1, recipeId: -1, stepId: -1, kind: 'voice', body: 'Sample body', mediaUrl: 'Sample mediaUrl', durationSeconds: 1, recordedAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, recipeId: -2, stepId: -2, kind: 'photo', body: 'Sample body', mediaUrl: 'Sample mediaUrl', durationSeconds: 2, recordedAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, recipeId: -3, stepId: -3, kind: 'video', body: 'Sample body', mediaUrl: 'Sample mediaUrl', durationSeconds: 3, recordedAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededMarginNote, (rows) => {
  if (rows.length && !marginNotes.value.length) marginNotes.value = [...rows];
}, { immediate: true });

export const marginNotesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'margin-note');
  if (mine.length) marginNotes.value = mine;
  return marginNotes.value;
});

export function addMarginNote(input = {}) {
  const row = { id: nextId(), entity: 'margin-note',
    recipeId: input.recipeId ?? '',
    stepId: input.stepId ?? '',
    kind: input.kind ?? '',
    body: input.body ?? '',
    mediaUrl: input.mediaUrl ?? '',
    durationSeconds: input.durationSeconds ?? 0,
    recordedAt: input.recordedAt ?? new Date().toISOString(),
  };
  marginNotes.value = [row, ...marginNotes.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveMarginNote(row) {
  marginNotes.value = marginNotes.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeMarginNote(id) {
  marginNotes.value = marginNotes.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findMarginNote = (id) =>
  marginNotes.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentMarginNote = computed(() => marginNotes.value.slice(0, 3));
export const marginNotesTitle = (row) => row?.body;
